import { useTranslations } from '@vocab/react';
import {
  Box,
  Column,
  Columns,
  Loader,
  Stack,
  Strong,
  Text,
  TextDropdown,
  TextLink,
} from 'braid-design-system';
import { useParams } from 'react-router';

import {
  buildMelwaysUrlResolverURI,
  type FlagReviewFunctionType,
  type Reviews,
  type SortOrder,
  useConfig,
} from '@seek/libs-shared';

import type { AppConfig } from '../../../../shared/config';

import { ReviewRow } from '../reviewRow/ReviewRow';
import translations from './.vocab';

interface Props {
  flagReview: FlagReviewFunctionType;
  onSortOrderChange: (value: SortOrder) => void;
  reviewsLoaded: boolean;
  sortOrder: SortOrder;
  trackRegisterClicked: () => void;
  trackSignInClicked: () => void;
  upvoteReview: ({
    reviewId,
    companyId,
  }: {
    companyId: string;
    reviewId: string;
  }) => Promise<void>;
  locationContextualName?: string;
  model?: Reviews;
}

export const ReviewsList = ({
  model,
  upvoteReview,
  flagReview,
  onSortOrderChange,
  sortOrder,
  locationContextualName,
}: Props) => {
  const { t } = useTranslations(translations);
  const config = useConfig<AppConfig>();
  const urlParams = useParams();

  const total = model?.paging?.total || 0;

  if (!model) {
    return <Loader />;
  }

  return (
    <Stack space="large">
      <Columns space="small" collapseBelow="tablet">
        <Column>
          {locationContextualName ? (
            <Text size="small">
              {t(
                'Showing <Strong>{total}</Strong> reviews for {location_contextual_name}',
                {
                  Strong: (children) => <Strong>{children}</Strong>,
                  total: `${total}`,
                  location_contextual_name: locationContextualName,
                },
              )}{' '}
              <TextLink
                weight="weak"
                href={buildMelwaysUrlResolverURI({
                  site: config.siteName,
                  language: config.language,
                  country: config.country,
                  path: `/companies/${urlParams.companySlug}/reviews`,
                  staging: config.environment === 'staging',
                })}
              >
                <Box display={{ mobile: 'block', desktop: 'inline' }}>
                  {t('See reviews from all locations')}
                </Box>
              </TextLink>
            </Text>
          ) : (
            <Text size="small">
              {t('Showing <Strong>{total}</Strong> reviews sorted by', {
                Strong: (children) => <Strong>{children}</Strong>,
                total: `${total}`,
              })}{' '}
              <Strong>{t(`${sortOrder}`)}</Strong>
            </Text>
          )}
        </Column>
        <Column width="content">
          <Text size="small">
            {t('Sort by')}{' '}
            <Strong>
              <TextDropdown
                id="sortDropdown"
                label={t('Sort by')}
                value={sortOrder}
                onChange={onSortOrderChange}
                options={[
                  {
                    text: t('most recent'),
                    value: 'most recent',
                  },
                  {
                    text: t('most helpful'),
                    value: 'most helpful',
                  },
                ]}
              />
            </Strong>
          </Text>
        </Column>
      </Columns>
      <Stack space="small">
        {model.reviews?.map((review, index) => (
          <ReviewRow
            key={index}
            model={review}
            isOnReviewPage={false}
            upvoteReview={upvoteReview}
            flagReview={flagReview}
            index={index}
          />
        ))}
      </Stack>
    </Stack>
  );
};
